import { clone } from 'ramda';

export interface ExpandUrlParamsOptions {
  removeOrphanedSearchItems?: boolean;
  encodeParamValues?: boolean;
  addUnusedParamsToSearch?: boolean;
  splitParamValuesOnComma?: boolean;
}

const defaultOptions: ExpandUrlParamsOptions = {
  removeOrphanedSearchItems: true,
  encodeParamValues: false,
  addUnusedParamsToSearch: false,
  splitParamValuesOnComma: true,
};

/**
 *
 * Process template into url
 *
 * @param urlTemplate - template to be processed with replacement values. Replaced template keys start with ':'
 * @param paramData - keys and values for replacement in template
 * @param [options] - options that determine how template is processed
 * @param [options.removeOrphanedSearchItems=true] - removes template search string items (after the ?) that aren't replaced
 * @param [options.encodeParamValues] - uri encode the param data values
 * @param [options.addUnusedParamsToSearch] - add unused param data to the search string
 * @param [options.splitParamValuesOnComma] - split param values on commas to be treated as array param values
 * @returns url string
 *
 */
export function expandUrlParams<T = Record<string, unknown>>(
  urlTemplate: string,
  paramData: T,
  options: ExpandUrlParamsOptions = {}
) {
  const mergedOptions: ExpandUrlParamsOptions = { ...defaultOptions, ...options };
  const url = new URL(urlTemplate);

  // cloning the param data is important because the unmatche param data
  // is actually manipulated and changes the params, which will
  // eventually lead to hard-to-debug side effects for callers
  const unmatchedParamData = clone(paramData) as Record<string, unknown>;

  let path = url.pathname;
  let searchString = decodeURIComponent(url.searchParams.toString());

  //
  // expand all path params with param data
  //
  const pathParts = path.split('/').map((part) => {
    const normPart = decodeURIComponent(part);

    if (normPart.startsWith(':')) {
      const dataKey = normPart.split(':')[1];
      const dataValue = (paramData as Record<string, unknown>)[dataKey];
      delete unmatchedParamData[dataKey];

      if (typeof dataValue !== 'undefined') {
        return dataValue;
      }
    }

    return normPart;
  });

  path = pathParts.join('/');

  //
  // expand all query params with param
  // data and remove any orphans, when applicable
  //

  const searchParams = searchString.split('&').reduce<string[]>((paramsTally, searchSegment) => {
    if (searchSegment.includes(':')) {
      const searchItemParts = searchSegment.split('=');
      const searchItemKey = searchItemParts[0];
      let searchItemValue = searchItemParts[1];
      const searchItemSubValues = searchItemValue.split(',');

      const newSearchItemSubValues = searchItemSubValues.reduce<string[]>((tally, item) => {
        if (item.startsWith(':')) {
          const dataKey = item.split(':')[1];
          const dataValue = (paramData as Record<string, unknown>)[dataKey];
          delete unmatchedParamData[dataKey];
          const newItem = typeof dataValue !== 'undefined' ? String(dataValue) : '';

          if (!mergedOptions.removeOrphanedSearchItems || newItem) {
            tally.push(newItem);
          }
        } else {
          tally.push(item);
        }

        return tally;
      }, []);

      searchItemValue = newSearchItemSubValues.join(',');

      const pushItem =
        !mergedOptions.removeOrphanedSearchItems || searchItemValue
          ? `${searchItemKey}=${
              mergedOptions.encodeParamValues ? encodeURIComponent(searchItemValue) : searchItemValue
            }`
          : undefined;

      if (pushItem) {
        paramsTally.push(pushItem);
      }
    } else {
      paramsTally.push(searchSegment);
    }

    return paramsTally.filter((p) => p);
  }, []);

  const additionalParams = mergedOptions.addUnusedParamsToSearch
    ? Object.keys(unmatchedParamData)
        .filter((k) => typeof unmatchedParamData[k] !== 'undefined')
        .flatMap((k) => {
          const paramVals = mergedOptions.splitParamValuesOnComma
            ? String(unmatchedParamData[k]).split(',')
            : [String(unmatchedParamData[k])];

          if (paramVals.length) {
            return paramVals.reduce<string[]>((acc, val) => {
              return [
                ...acc,
                `${k}=${mergedOptions.encodeParamValues ? encodeURIComponent(String(val)) : String(val)}`,
              ];
            }, []);
          } else {
            return `${k}=${
              mergedOptions.encodeParamValues
                ? encodeURIComponent(String(paramVals[0]))
                : String(paramVals[0])
            }`;
          }
        })
    : [];

  searchString = [...searchParams, ...additionalParams].join('&');

  const newUrlString = `${url.origin}${path}${url.hash ? url.hash : ''}${
    searchString ? `?${searchString}` : ''
  }`;

  return newUrlString;
}
